import { Button } from "@sellar-io/components";
import type { ReactElement } from "react";
import { useNavigate } from "react-router";
import { Alert } from "../../../../../components/alert/Alert";
import {
  Dialog,
  DialogContent,
  DialogCTA,
  DialogNavigation,
} from "../../../../../components/modal/Dialog";
import { LAYOUT_GROUPING } from "../../../../../constants/routes";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";
import { useStorefrontTagDelete } from "../../../../../hooks/api/v2/storefrontTags/useStorefrontTagDelete";

export const DeleteGroupDialog = ({
  active,
  onDismiss,
  groupId,
  groupName,
}: {
  active: boolean;
  onDismiss: () => void;
  groupId: number;
  groupName: string;
}): ReactElement => {
  const navigate = useNavigate();
  const deleteMutation = useStorefrontTagDelete(groupId);
  const dismiss = () => {
    deleteMutation.reset();
    onDismiss();
  };

  return (
    <Dialog active={active} onDismiss={dismiss}>
      <DialogNavigation
        onClickLeftIcon={dismiss}
        leftIcon="CHEVRON_DOWN"
        title={`Delete ${groupName}?`}
      />
      <DialogContent className="flex flex-col gap-4 p-4">
        <p>
          This permanently removes the empty group from your storefront. This
          action cannot be undone.
        </p>
        {deleteMutation.isError && (
          <Alert
            type="error"
            title="The group could not be deleted"
            message={
              apiV2AssertError(deleteMutation.error)?.response?.data.message ??
              "Try again."
            }
          />
        )}
      </DialogContent>
      <DialogCTA>
        <Button
          color="transparent"
          disabled={deleteMutation.isPending}
          onClick={dismiss}
        >
          Cancel
        </Button>
        <Button
          color="red"
          loading={deleteMutation.isPending}
          onClick={() =>
            deleteMutation.mutate(undefined, {
              onSuccess: () => {
                navigate(LAYOUT_GROUPING);
                onDismiss();
              },
            })
          }
        >
          Delete group
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
